// Back-navigation: контекст «откуда пришёл» для detail-страниц.
//
// Из workspace материала (apps/web/src/components/materials/panels/*) кнопки
// «Открыть тест / набор карточек / конспект / аудио-резюме» ведут на
// отдельные routes (`/notes/$id`, `/tests/take/$id`, `/flashcards/sets/$id`
// и т.д.). По умолчанию back-link с detail-страницы возвращал в общий
// список (`/notes`, `/tests`, …), теряя контекст материала. Через
// `?from=material:<materialId>` контекст явно передаётся в URL и
// читается на detail-route → back-button уводит обратно в материал.
//
// Формат значения: `<kind>:<id>` или `<kind>:<id1>:<id2>`.
// Поддерживаемые kinds:
//   material:<materialId>                — workspace материала
//   subsection:<planId>:<subId>          — ТЕМА курса (`/courses/$id/topics/$topicId`)
// При расширении — добавь в `resolveBackFrom` и в типы.
//
// ВНИМАНИЕ: строковый префикс `subsection:` намеренно НЕ переименован в
// `topic:` — обучающая система (`docs/plans/LEARNING-SYSTEM-UI.md`) сменила
// только URL-адресацию (`/study-plan/$id/subsection/$subId` →
// `/courses/$courseId/topics/$topicId`). Уже разосланные ссылки, закладки и
// открытые вкладки несут старый префикс в `?from=`; ломать их нельзя.
// Меняется только цель (`to`/`params`), которую отдаёт `resolveBackFrom`.

import { z } from "zod";

/** Zod-схема для `validateSearch` route'ов. Merge'ить с локальной схемой роута. */
export const fromSearchSchema = z.object({
	from: z
		.string()
		.regex(/^[a-z]+:[0-9a-fA-F-]+(:[0-9a-fA-F-]+)?$/, "from must be <kind>:<id>[:<id2>]")
		.optional(),
});

export type FromParam = z.infer<typeof fromSearchSchema>["from"];

/** Сформировать значение `from` для linking из workspace материала. */
export function fromMaterial(materialId: string): string {
	return `material:${materialId}`;
}

/**
 * Сформировать значение `from` для linking из темы курса.
 * `planId` = id курса, `subsectionId` = id темы (модель «курс == study_plan»,
 * «тема == study_subsection» — BLUEPRINT §0).
 */
export function fromSubsection(planId: string, subsectionId: string): string {
	return `subsection:${planId}:${subsectionId}`;
}

export type BackFromTarget =
	| { kind: "material"; materialId: string; to: "/materials/$materialId" }
	| {
			kind: "subsection";
			planId: string;
			subsectionId: string;
			to: "/courses/$courseId/topics/$topicId";
			params: { courseId: string; topicId: string };
	  };

/**
 * Распарсить `from` и вернуть TanStack-Router-совместимый back-target.
 * `null` — нет origin, callsite использует свой fallback на list.
 */
export function resolveBackFrom(from: FromParam): BackFromTarget | null {
	if (!from) return null;
	if (from.startsWith("material:")) {
		const materialId = from.slice("material:".length);
		if (!materialId || materialId.includes(":")) return null;
		return { kind: "material", materialId, to: "/materials/$materialId" };
	}
	if (from.startsWith("subsection:")) {
		const rest = from.slice("subsection:".length);
		const [planId, subsectionId] = rest.split(":");
		if (!planId || !subsectionId) return null;
		return {
			kind: "subsection",
			planId,
			subsectionId,
			to: "/courses/$courseId/topics/$topicId",
			params: { courseId: planId, topicId: subsectionId },
		};
	}
	return null;
}
